import { useMemo } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  useCreateVenuaPlannerProject,
  useUpdateVenuaPlannerProject,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import {
  buildProjectFormSchema,
  emptyProjectFormValues,
  type ProjectFormValues,
} from './projectFormSchema';
import { toProjectRequestBody } from './toProjectRequest';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Name: 'name',
  DestinationIds: 'destinations',
};

interface UseProjectFormOptions {
  /** Bez `projectId` = zakládání, s `projectId` = editace. */
  projectId?: string;
  /** Výchozí hodnoty (pro editaci namapované z detailu projektu). */
  initialValues?: ProjectFormValues;
  /** Voláno po úspěšném uložení. */
  onSaved: () => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na konkrétní pole (např. síťová chyba). */
  onError?: (error: unknown) => void;
}

/**
 * Zapouzdřuje formulářovou logiku projektu (sdílená pro zakládání i editaci) – TanStack Form
 * se Zod validací, create/update mutace a promítnutí serverových validačních chyb do polí.
 */
export function useProjectForm({
  projectId,
  initialValues,
  onSaved,
  onError,
}: UseProjectFormOptions) {
  const { t } = useTranslation('planner');
  const schema = useMemo(() => buildProjectFormSchema(t), [t]);

  const createMutation = useCreateVenuaPlannerProject();
  const updateMutation = useUpdateVenuaPlannerProject();

  const form = useForm({
    defaultValues: initialValues ?? emptyProjectFormValues(),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      const data = toProjectRequestBody(value);
      try {
        if (projectId) {
          await updateMutation.mutateAsync({ projectId, data });
        } else {
          await createMutation.mutateAsync({ data });
        }
        onSaved();
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  return {
    form,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
  };
}
